import { useQuery } from "@tanstack/react-query";
import { Building2, MailQuestion, Users } from "lucide-react";
import type { ReactNode } from "react";

import {
  getGrantTeamManagerOverview,
  grantTeamManagerQueryKey,
  GrantTeamManagerForbiddenError,
} from "@/api/grant_team_manager";
import { useMunicipality } from "@/context/MunicipalityContext";
import { Badge } from "@/components/ui/badge";

function OverviewRow({
  title,
  subtitle,
  badge,
  testId,
}: {
  title: string;
  subtitle: string;
  badge: ReactNode;
  testId: string;
}) {
  return (
    <div
      className="flex items-start justify-between gap-3 rounded-md border bg-card px-3 py-2"
      data-testid={testId}
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{title}</p>
        <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
      </div>
      <Badge variant="outline" className="shrink-0 font-normal">
        {badge}
      </Badge>
    </div>
  );
}

function EmptyState({ icon: Icon, message }: { icon: typeof Users; message: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed px-4 py-8 text-center">
      <Icon className="h-6 w-6 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

/**
 * The contained Team Manager area (ENG-1838). Deliberately its own tab rather
 * than controls sprinkled through Tracker, Discovery, and Milestones: those
 * surfaces stay free of admin, and one gated area keeps the blast radius of a
 * Team Manager action obvious.
 */
export function GrantTeamManagerPanel() {
  const { municipality } = useMunicipality();
  const municipalityId = municipality?.id;

  const {
    data: overview,
    isPending,
    error,
  } = useQuery({
    queryKey: grantTeamManagerQueryKey(municipalityId),
    queryFn: () => getGrantTeamManagerOverview(municipalityId!),
    enabled: Boolean(municipalityId),
    // A 403 from the gate is a settled answer, not a transient failure. The
    // app-wide default is retry: 3, which would fire three more identical
    // requests at the gate after the denial UI already has what it needs.
    retry: (failureCount, queryError) =>
      !(queryError instanceof GrantTeamManagerForbiddenError) && failureCount < 3,
  });

  // isPending, not isLoading: a disabled query (municipality still resolving)
  // is pending-without-fetching, and must read as loading — not as a denial.
  let body: ReactNode;
  if (!municipalityId || isPending) {
    body = <p className="text-sm text-muted-foreground">Loading the Team Manager area…</p>;
  } else if (error instanceof GrantTeamManagerForbiddenError) {
    // The backend gate is the access control, so a denial lands here rather
    // than in a hidden tab. Say so plainly instead of showing an empty area.
    body = (
      <p className="text-sm text-destructive">
        You don&rsquo;t manage this team&rsquo;s grant program.
      </p>
    );
  } else if (error || !overview) {
    body = (
      <p className="text-sm text-destructive">
        Could not load the Team Manager area. Please try again.
      </p>
    );
  } else {
    body = (
      <>
        <p className="text-sm text-muted-foreground">
          Administer your team&rsquo;s grant program. These controls are not available from Tracker,
          Discovery, or milestones.
        </p>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Departments</h3>
          <p className="text-xs text-muted-foreground">
            Departments on this team, with the people who have joined them.
          </p>
          {overview.departments.length === 0 ? (
            <EmptyState icon={Building2} message="No departments on this team yet." />
          ) : (
            <div className="space-y-2">
              {overview.departments.map((department) => (
                <OverviewRow
                  key={department.id}
                  title={department.name}
                  subtitle={
                    department.opportunity_count === 1
                      ? "1 opportunity"
                      : `${department.opportunity_count} opportunities`
                  }
                  badge={
                    <>
                      <Users className="mr-1 h-3 w-3" />
                      {department.member_count}
                    </>
                  }
                  testId="team-manager-department-row"
                />
              ))}
            </div>
          )}
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Outside support requests</h3>
          <p className="text-xs text-muted-foreground">
            Queued invites for people at partner organizations. Ordinal staff send or reject these.
          </p>
          {overview.pending_invite_requests.length === 0 ? (
            <EmptyState icon={MailQuestion} message="No outside invites are waiting." />
          ) : (
            <div className="space-y-2">
              {overview.pending_invite_requests.map((request) => (
                <OverviewRow
                  key={request.id}
                  title={request.email}
                  subtitle={request.external_organization_name}
                  badge="Awaiting Ordinal"
                  testId="team-manager-invite-request-row"
                />
              ))}
            </div>
          )}
        </section>
      </>
    );
  }

  return (
    <div
      className="ordinal-scrollbar flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto"
      data-testid="grant-team-manager-panel"
    >
      {body}
    </div>
  );
}
